// @react
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
// @types
import { GetUserQueryResult } from 'lib/graphql/graphqlTypes'
// @graphQL
// @libraries
import { DevTool } from '@hookform/devtools'
import { makeStyles } from '@material-ui/core/styles'
// @commons
// @components
import Container from '@bit/atd.web.container'
import Grid from '@bit/atd.web.grid'
import Spacer from '@bit/atd.web.spacer'
import Box from '@bit/atd.web.box'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import Navigation from 'modules/onBoarding/components/_common/Navigation'
import AboutFields from 'modules/common/forms/fields/profile/About'
import WatchPublicProfile from 'modules/common/forms/banners/WatchPublicProfile'
import useBioFormWrapper from 'modules/common/forms/hooks/useBioFormWrapper'
import PublicVisibility from 'modules/common/components/ProfileVisibility'
import { Direction, NavigationContext } from 'modules/common/context/navigation'
import { Slide } from '@material-ui/core'
import useNavigation from 'modules/onBoarding/hooks/useNavigation'
import { ProfileFormValues } from 'modules/common/forms/types/profile'

/**
 * OnBoarding - Step 1 - Profile
 * https://www.figma.com/file/qfo4N93iI12FDinKJQaXKH/ATD-MyATD-Desktop-2020?node-id=174%3A47986
 */

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '768px',
    margin: '0 auto',
    '& .MuiPaper-root': {
      width: '100%',
    },
  },
}))

type PropsType = {
  data?: GetUserQueryResult['data']
  isLoading: boolean
  refetch: any
}

const Profile = ({
  data: userData,
  isLoading,
  refetch,
}: PropsType): ReactElement => {
  /******************************************
   * STATE
   ******************************************/

  // Stores File object from profile picture
  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [slidingIn, setSlidingIn] = useState<boolean>(false)
  const [destination, setDestination] = useState<string>('')
  const [direction, setDirection] = useState<Direction>(Direction.LEFT)

  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()
  const history = useHistory()
  const navigation = useContext(NavigationContext)

  useEffect(() => {
    setSlidingIn(true)
  }, [])

  const {
    formMethods,
    control,
    setImageFile,
    userProfileUpdateHandler,
  } = useBioFormWrapper({
    data: userData,
    isLoading,
    refetch,
  })

  // React Hook Form
  const {
    formState: { dirtyFields, errors },
  } = formMethods
  const isFormDirty = Object.keys(dirtyFields).length > 0

  const { handleNextClick, handleSaveExit } = useNavigation({
    setDestination,
    setDirection,
    setSlidingIn,
    setIsSubmitting,
    isFormDirty,
    updateHandler: userProfileUpdateHandler,
  })

  /******************************************
   * RENDER
   ******************************************/

  /**
   *
   * @param content
   * @param isLoading
   */
  const box = (content: ReactElement, isLoading: boolean) => {
    return <Box>{!isLoading ? content : <SkeletonLoader />}</Box>
  }

  return (
    <>
      {process.env.DEBUG !== 'true' && <DevTool control={control} />}
      <Spacer size={32} fullWidth />

      <form>
        <Slide
          in={slidingIn}
          timeout={500}
          direction={navigation.direction}
          unmountOnExit
          onExited={() => {
            navigation.setDirection(direction)
            history.push(destination)
          }}
        >
          <div>
            <Container>
              <Grid className={classes.container} container>
                {box(
                  <PublicVisibility isLoading={isLoading} data={userData} />,
                  isLoading,
                )}

                <Spacer size={24} fullWidth />

                {box(
                  <>
                    <Grid container spacing={4}>
                      <Grid xs={12} item>
                        <WatchPublicProfile control={control} />
                      </Grid>

                      <AboutFields
                        editing
                        control={control}
                        formMethods={formMethods}
                        errors={errors}
                        setImageFile={setImageFile}
                        userData={userData}
                      />
                    </Grid>
                  </>,
                  isLoading,
                )}
              </Grid>
            </Container>
          </div>
        </Slide>

        <Navigation
          handleExitOnClick={formMethods.handleSubmit(
            (data: ProfileFormValues) => handleSaveExit(data),
            (errors) => handleSaveExit(undefined, errors),
          )}
          handleNextOnClick={formMethods.handleSubmit(
            (data: ProfileFormValues) => handleNextClick(data),
            (errors) => handleNextClick(undefined, errors),
          )}
          nextLabel="Next"
          nextPage="Work"
          isSubmitting={isMutationSubmitting}
          nextDisable={isLoading || isMutationSubmitting}
          exitDisable={isLoading || isMutationSubmitting}
          prevDisable={isLoading || isMutationSubmitting}
        />
      </form>
    </>
  )
}

export default Profile
